import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Lock, Eye, EyeOff } from 'lucide-react';

interface PasswordProtectedProps {
  children: React.ReactNode;
}

const PasswordProtected: React.FC<PasswordProtectedProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const COOKIE_NAME = 'dashboard_auth';
  const REQUIRED_PASSWORD = process.env.REACT_APP_DASHBOARD_PASSWORD;

  // Cookie utility functions
  const setCookie = (name: string, value: string) => {
    // Set cookie with no expiration date (persistent)
    document.cookie = `${name}=${value}; path=/; SameSite=Strict; Secure=${window.location.protocol === 'https:'}`;
  };

  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  };

  // Check for existing authentication on component mount
  useEffect(() => {
    const authCookie = getCookie(COOKIE_NAME);
    if (authCookie === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!REQUIRED_PASSWORD) {
      setError('Dashboard password not configured');
      return;
    }

    if (password === REQUIRED_PASSWORD) {
      setIsAuthenticated(true);
      setCookie(COOKIE_NAME, 'authenticated');
      setPassword(''); // Clear password from state
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Lock className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-white text-2xl">Dashboard Access</CardTitle>
            <p className="text-gray-400">Enter password to access dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter dashboard password"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-12"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {error && (
                <div className="text-red-400 text-sm text-center bg-red-900/20 border border-red-800 rounded-lg p-3">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 px-4 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-800"
              >
                Access Dashboard
              </button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-400">
              <p>Password will be saved for future visits</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default PasswordProtected;